import React, { useCallback, useEffect, useState } from 'react'
import { NativeSyntheticEvent, TextInputChangeEventData } from 'react-native'
import { connect, ConnectedProps } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { KEYS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'

import { GoSearchModal } from './GoSearchModal'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { RootState, useAppDispatch } from '../../../data/store'
import { setSelectedClientId } from '../../../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { useAllClientsData } from '../../../lyraTherapy/clients/data/hooks/useAllClientsData'
import { getIsPrivacyGuardActive } from '../../../lyraTherapy/data/ltVideoSelectors'
import { CLIENT_HOME } from '../../constants/routingConstants'
import { searchForClients } from '../../utils/utils'

/**
 * Open a modal giving the user a typehead to search for clients and navigate to them.
 */
const GoSearch: React.FC<ConnectedProps<typeof connector>> = ({ isPrivacyGuardActive }) => {
  const [clients] = useAllClientsData()

  const [isGoOpen, setGoOpen] = useState(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const onHitClick = (hit: ClientListClientObject) => {
    track({ event: 'NAVIGATE_TO_CLIENT_FROM_GO_SEARCH_BAR', properties: { client: hit } })
    dispatch(setSelectedClientId(hit.id))
    navigate(CLIENT_HOME.route)
    setGoOpen(false)
  }

  // Close Search if starting a video session.
  useEffect(() => {
    if (isPrivacyGuardActive) {
      setGoOpen(false)
    }
  }, [isPrivacyGuardActive])

  useEffect(() => {
    // Open the modal via the button on the navigation
    const onOpenGoSearch = () => {
      track({ event: 'OPEN_GO_SEARCH_BAR' })
      setGoOpen(true)
    }

    // Open the modal via CTRL/ALT + SPACE
    const onMagicKeys = (event: { key: string; ctrlKey: boolean; altKey: boolean }) => {
      const isCtrlSpace = event.key === KEYS.SPACE && event.ctrlKey
      const isAltSpace = event.key === KEYS.SPACE && event.altKey

      if ((isCtrlSpace || isAltSpace) && isGoOpen === false) {
        track({ event: 'OPEN_GO_SEARCH_BAR' })
        setGoOpen(true)
      }
    }

    if (!isPrivacyGuardActive) {
      document.body.addEventListener('openGoSearch', onOpenGoSearch)
      document.body.addEventListener('keydown', onMagicKeys)
    }

    return () => {
      document.body.removeEventListener('openGoSearch', onOpenGoSearch)
      document.body.removeEventListener('keydown', onMagicKeys)
    }
  }, [isGoOpen, isPrivacyGuardActive])

  const onSearch = useCallback(
    (event: NativeSyntheticEvent<TextInputChangeEventData>) => {
      return searchForClients({ clients, event })
    },
    [clients],
  )

  const handleCloseModal = () => {
    track({ event: 'CLOSE_GO_SEARCH_BAR' })
    setGoOpen(false)
  }

  if (!isGoOpen) {
    return null
  }
  return (
    <GoSearchModal
      isSearchBarOpen={isGoOpen}
      onHitClick={onHitClick}
      onRequestClose={handleCloseModal}
      onSearch={(event: NativeSyntheticEvent<TextInputChangeEventData>) => onSearch(event)}
      isPrivacyGuardActive={isPrivacyGuardActive}
    />
  )
}

type StateProps = {
  isPrivacyGuardActive: boolean
}
const mapStateToProps = (state: RootState): StateProps => ({
  // Block client search during a video session or while privacy guard is on
  isPrivacyGuardActive: getIsPrivacyGuardActive(state),
})

const connector = connect(mapStateToProps)
export default connector(GoSearch)
